/**
 * SettingsLayout Component
 *
 * Provides the layout wrapper for all settings pages including:
 * - Sidebar navigation with 4 items
 * - Back to App link
 * - Beta badge footer
 *
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section "Settings Navigation Sidebar"
 */

import { NavLink, useNavigate } from 'react-router-dom';
import {
  Heart,
  User,
  Shield,
  FileText,
  Bell,
  ArrowRight,
  Star,
} from 'lucide-react';

interface SettingsLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  {
    path: '/settings',
    label: 'Care Recipient Profile',
    icon: <Heart className="w-5 h-5" />,
  },
  {
    path: '/profile',
    label: 'My Profile',
    icon: <User className="w-5 h-5" />,
  },
  {
    path: '/settings/security',
    label: 'Account Security',
    icon: <Shield className="w-5 h-5" />,
  },
  {
    path: '/settings/privacy',
    label: 'Data & Privacy',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    path: '/settings/notifications',
    label: 'Notifications',
    icon: <Bell className="w-5 h-5" />,
  },
];

export function SettingsLayout({ children }: SettingsLayoutProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar */}
      <aside className="w-[260px] bg-white border-r border-soft-neutral flex flex-col py-6 flex-shrink-0">
        {/* Sidebar Header */}
        <div className="px-6 pb-6 border-b border-soft-neutral mb-4">
          <h1 className="text-2xl font-bold text-ink">Settings</h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/settings'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer text-[15px] font-medium transition-all mb-1 ${
                  isActive
                    ? 'bg-airy-gradient text-evergreen font-semibold'
                    : 'text-muted hover:bg-airy-gradient hover:text-ink'
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="px-6 pt-4 border-t border-soft-neutral mt-auto">
          <span className="inline-flex items-center gap-1.5 bg-soft-neutral text-muted px-3 py-1.5 rounded-full text-xs font-semibold">
            <Star className="w-3 h-3" />
            Free Beta
          </span>
        </div>
      </aside>

      {/* Main Content */}
      <main id="main-content" tabIndex={-1} className="flex-1 py-6 px-10 max-w-[900px] outline-none">
        {/* Back to App Header */}
        <div className="flex justify-end mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-slate-ink text-sm font-semibold flex items-center gap-1.5 hover:text-evergreen transition-colors"
          >
            Back to App
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Page Content */}
        {children}
      </main>
    </div>
  );
}

export default SettingsLayout;
